import { apiClient } from './client';

export interface QuoteRequest {
  instrumentId: string;
  product: string;
  stake: number;
  currency: string;
  product_params: Record<string, any>;
}

export interface QuoteResponse {
  instrumentId: string;
  product: string;
  stake: number;
  payout: number;
  return_percent: number;
  spot: number;
  strike: number;
  product_params: Record<string, any>;
  expiry_time: string;
  base_probability: number;
  risk_markup: number;
  commission: number;
  currency: string;
  timestamp: string;
}

export interface OrderValidationRequest {
  accountId: string;
  instrumentId: string;
  product: string;
  stake: number;
  currency: string;
  product_params: Record<string, any>;
}

export interface OrderValidationResponse {
  valid: boolean;
  stake: number;
  payout: number;
  return_percent: number;
  spot: number;
  strike: number;
  expiry_time: string;
  base_probability: number;
  risk_markup: number;
  commission: number;
  validation: Record<string, string>;
  error: string | null;
}

export class QuoteService {
  /**
   * Fetch a live quote for a given product, instrument, and parameters.
   * POST /v1/quotes
   */
  async getQuote(req: QuoteRequest): Promise<QuoteResponse> {
    try {
      const response = await apiClient.post<QuoteResponse>('/v1/quotes', req);
      return response;
    } catch (error) {
      console.error('[QuoteService] Quote request failed:', error);
      throw error;
    }
  }

  /**
   * Validate order parameters without placing an actual order.
   * POST /v1/orders/validate
   */
  async validateOrder(req: OrderValidationRequest): Promise<OrderValidationResponse> {
    try {
      const response = await apiClient.post<OrderValidationResponse>('/v1/orders/validate', req);
      return response;
    } catch (error) {
      console.error('[QuoteService] Order validation failed:', error);
      throw error;
    }
  }

  /**
   * Get real-time pricing with parameter validation
   */
  async getQuoteWithValidation(req: QuoteRequest & { accountId: string }): Promise<{
    quote: QuoteResponse;
    validation: OrderValidationResponse;
  }> {
    try {
      // Run quote and validation in parallel
      const [quote, validation] = await Promise.all([
        this.getQuote({
          instrumentId: req.instrumentId,
          product: req.product,
          stake: req.stake,
          currency: req.currency,
          product_params: req.product_params
        }),
        this.validateOrder({
          accountId: req.accountId,
          instrumentId: req.instrumentId,
          product: req.product,
          stake: req.stake,
          currency: req.currency,
          product_params: req.product_params
        })
      ]);

      return { quote, validation };
    } catch (error) {
      console.error('[QuoteService] Quote with validation failed:', error);
      throw error;
    }
  }

  /**
   * Format product parameters for API based on product type
   */
  formatProductParams(product: string, params: Record<string, any>): Record<string, any> {
    const formatted: Record<string, any> = { ...params };

    switch (product) {
      case 'rise_fall':
        // Ensure correct parameter names for Rise/Fall
        if (params.direction) {
          formatted.contract_type = params.direction;
          delete formatted.direction;
        }
        break;

      case 'higher_lower':
        // Ensure barrier is properly formatted
        if (params.barrier && typeof params.barrier === 'string') {
          formatted.barrier = params.barrier;
        }
        break;

      case 'matches_differs':
      case 'even_odd':
      case 'over_under':
        // Ensure tick count is within valid range
        if (params.tick_count) {
          formatted.tick_count = Math.max(1, Math.min(10, params.tick_count));
        }
        break;

      case 'accumulator':
        // Ensure growth rate is valid
        if (params.growth_rate) {
          formatted.growth_rate = Math.max(1, Math.min(5, params.growth_rate));
        }
        // Ensure max_ticks is set
        if (!params.max_ticks) {
          formatted.max_ticks = 250;
        }
        break;
    }

    return formatted;
  }

  /**
   * Get duration in minutes for API
   */
  parseDuration(durationString: string): { unit: string; value: number } {
    const duration = durationString.toLowerCase();
    
    if (duration.includes('minute')) {
      const value = parseInt(duration) || 15;
      return { unit: 'minutes', value };
    }
    
    if (duration.includes('hour')) {
      const value = parseInt(duration) || 1;
      return { unit: 'hours', value };
    }
    
    if (duration.includes('day')) {
      const value = parseInt(duration) || 1;
      return { unit: 'days', value };
    }
    
    // Default to minutes
    const value = parseInt(duration) || 15;
    return { unit: 'minutes', value };
  }

  /**
   * Calculate return percentage from quote
   */
  calculateReturnPercent(stake: number, payout: number): number {
    if (stake <= 0) return 0;
    return ((payout - stake) / stake) * 100;
  }

  /**
   * Format quote for UI display
   */
  formatQuoteForUI(quote: QuoteResponse): {
    stake: number;
    payout: number;
    profit: number;
    returnPercent: string;
    commission: number;
    spot: number;
    expiryTime: string;
  } {
    const profit = quote.payout - quote.stake;
    const returnPercent = quote.return_percent?.toFixed(1) || '0.0';

    return {
      stake: quote.stake,
      payout: quote.payout,
      profit,
      returnPercent,
      commission: quote.commission,
      spot: quote.spot,
      expiryTime: quote.expiry_time
    };
  }
}

// Export singleton instance
export const quoteService = new QuoteService();
